import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Badge, 
  Nav,
  Alert, 
  ListGroup, 
  Table
} from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  FaArrowLeft, 
  FaEdit, 
  FaSave, 
  FaVial, 
  FaHistory, 
  FaTimes, 
  FaServer, 
  FaCheckCircle, 
  FaSync,
  FaTimesCircle, 
  FaPlus,  
  FaFileAlt, 
  FaCogs,
  FaCode,
  FaProjectDiagram
} from 'react-icons/fa';
import * as api from '../services/api';
import Spinner from '../components/Spinner';
import './MicroAgentDevelopment.css';
import { Mermaid } from 'mdx-mermaid/Mermaid';

const MiniAgentDevelopment = () => {
  const { agentId, miniAgentId } = useParams();
  const navigate = useNavigate();
  
  const [miniAgent, setMiniAgent] = useState(null);
  const [parentAgent, setParentAgent] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [promptContent, setPromptContent] = useState('');
  const [environmentFunctions, setEnvironmentFunctions] = useState([]);
  const [selectedFunction, setSelectedFunction] = useState(null);
  const [environmentVariables, setEnvironmentVariables] = useState({});
  const [configFiles, setConfigFiles] = useState([]);
  const [selectedConfigFile, setSelectedConfigFile] = useState(null);
  const [configContent, setConfigContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeploying, setIsDeploying] = useState({ dev: false, prod: false });
  const [activeTab, setActiveTab] = useState('prompts');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVarsEditor, setShowVarsEditor] = useState(false);
  const [showPromptsEditor, setShowPromptsEditor] = useState(false);
  const [showConfigEditor, setShowConfigEditor] = useState(false);
  const [miniConfigData, setMiniConfigData] = useState({});
  const [metadataContent, setMetadataContent] = useState({});
  const [lambdaFunctions, setLambdaFunctions] = useState({});
  const [selectedLambdaFunction, setSelectedLambdaFunction] = useState(null);
  const [lambdaFunctionCode, setLambdaFunctionCode] = useState('');
  const [loadingLambda, setLoadingLambda] = useState(false);
  const [showLambdaEditor, setShowLambdaEditor] = useState(false);
  const [stateMachine, setStateMachine] = useState(null);
  const [loadingStateMachine, setLoadingStateMachine] = useState(false);
  const [mermaidDiagram, setMermaidDiagram] = useState('');
  const [selectedEnvironment, setSelectedEnvironment] = useState('DEV');

  const extractFromResponse = (response, ...keys) => {
    if (!response) return null;
    
    console.log('Extracting from response structure:', Object.keys(response));
    
    const paths = [
      response,
      response.response,
      response.body,
      response.data,
      response.body?.data?.test_results?.endpoint_response,
      response.data?.test_results?.endpoint_response,
      (() => {
        try {
          if (response.body?.data?.test_results?.endpoint_response?.body && 
              typeof response.body.data.test_results.endpoint_response.body === 'string') {
            return JSON.parse(response.body.data.test_results.endpoint_response.body);
          }
          if (response.data?.test_results?.endpoint_response?.body && 
              typeof response.data.test_results.endpoint_response.body === 'string') {
            return JSON.parse(response.data.test_results.endpoint_response.body);
          }
          return null;
        } catch (e) {
          console.warn('Failed to parse nested JSON body:', e);
          return null;
        }
      })()
    ].filter(Boolean);
    
    for (const path of paths) {
      if (!path) continue;
      
      let currentPath = path;
      if (typeof currentPath === 'string') {
        try {
          currentPath = JSON.parse(currentPath);
        } catch (e) {
          continue;
        }
      }
      
      let value = currentPath;
      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
          value = value[key];
        } else {
          value = null;
          break;
        }
      }
      
      if (value !== null && value !== undefined) {
        console.log(`Successfully extracted data from path for keys: ${keys.join('.')}`);
        return value;
      }
    }
    
    console.warn(`Could not extract data for keys: ${keys.join('.')}`);
    return null;
  };

  const isValidJson = (str) => {
    if (!str || str.trim() === '') return true;
    try {
      JSON.parse(str.trim());
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleFormatJson = () => {
    try {
      const parsed = JSON.parse(configContent.trim());
      setConfigContent(JSON.stringify(parsed, null, 2));
      toast.success('JSON formatted successfully');
    } catch (e) {
      toast.error('Cannot format: Invalid JSON');
    }
  };

  const handleConfigKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
      const newValue = configContent.substring(0, start) + '  ' + configContent.substring(end);
      setConfigContent(newValue);
      setTimeout(() => {
        e.target.selectionStart = start + 2;
        e.target.selectionEnd = start + 2;
      }, 0);
    }
  };

  useEffect(() => {
    const fetchMiniAgentDetails = async () => {
      if (!agentId || !miniAgentId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        try {
          const agentsResponse = await api.getAgents();
          
          let parentAgentData = null;
          
          if (agentsResponse && agentsResponse.success && agentsResponse.agents) {
            parentAgentData = agentsResponse.agents.find(a => 
              a.uniqueIdentifier === agentId || 
              a.agentName === agentId || 
              a.repoName === agentId
            );
          } else if (agentsResponse && agentsResponse.body && agentsResponse.body.agents) {
            parentAgentData = agentsResponse.body.agents.find(a => 
              a.uniqueIdentifier === agentId || 
              a.agentName === agentId || 
              a.repoName === agentId
            );
          } else if (agentsResponse && agentsResponse.agents) {
            parentAgentData = agentsResponse.agents.find(a => 
              a.uniqueIdentifier === agentId || 
              a.agentName === agentId || 
              a.repoName === agentId
            );
          }
          
          if (parentAgentData) {
            setParentAgent({
              id: parentAgentData.uniqueIdentifier,
              name: parentAgentData.agentName || parentAgentData.repoName,
              description: parentAgentData.agentDescription,
              repoName: parentAgentData.repoName || "AgentDeveloper",
              awsAccount: parentAgentData.awsAccountNumber || "851725399969",
              awsRegion: parentAgentData.awsRegion || "us-west-2",
              repoAccount: parentAgentData.repoAccount || "materassi",
              cdkQualifier: parentAgentData.cdkQualifier
            });
          } else {
            setParentAgent({
              id: agentId,
              name: "AgentDeveloper",
              repoName: "AgentDeveloper",
              awsAccount: "851725399969",
              awsRegion: "us-west-2",
              repoAccount: "materassi"
            });
          }
        } catch (err) {
          console.error('Error fetching parent agent details:', err);
          setParentAgent({
            id: agentId,
            name: "AgentDeveloper",
            repoName: "AgentDeveloper",
            awsAccount: "851725399969",
            awsRegion: "us-west-2",
            repoAccount: "materassi"
          });
        }

        try {
          console.log(`Fetching mini agent details for micro_agent_id: ${miniAgentId}`);
          const miniAgentResponse = await api.getMicroAgentsbyId(miniAgentId);
          
          if (miniAgentResponse && miniAgentResponse.microAgents && miniAgentResponse.microAgents.length > 0) {
            const miniAgentData = miniAgentResponse.microAgents[0];
            
            if (!miniAgentData.isMiniAgent) {
              setError('This is a micro-agent, not a mini-agent. Redirecting...');
              setTimeout(() => {
                navigate(`/agents/${agentId}/micro-agents/${miniAgentId}`);
              }, 2000);
              return;
            }
            
            const normalizedMiniAgent = {
              id: miniAgentData.uniqueIdentifier,
              agentNumber: miniAgentData.agentNumber || '000',
              name: miniAgentData.microAgentName || 'Untitled Agent',
              description: miniAgentData.microAgentDescription || 'No description available',
              parentAgentName: miniAgentData.parentAgentName || 'AgentDeveloper',
              commissioned: miniAgentData.commissioned || false,
              devStatus: miniAgentData.deployedInDev ? 'deployed' : 'not deployed',
              prodStatus: miniAgentData.deployedInProd ? 'deployed' : 'not deployed',
              isMiniAgent: miniAgentData.isMiniAgent || false
            };
            
            setMiniAgent(normalizedMiniAgent);
            
            if (miniAgentData.agentNumber) {
              fetchMiniConfigFiles(miniAgentData.agentNumber);
            }
          } else {
            throw new Error('Mini agent not found');
          }
        } catch (err) {
          console.error('Error fetching mini agent details:', err);
          setError('Failed to load mini agent details. Please try again.');
          throw err;
        }

        try {
          const promptsResponse = await api.getPromptContent(agentId, 'mini-agents', miniAgentId);
          
          if (promptsResponse && promptsResponse.prompts) {
            setPrompts(Object.keys(promptsResponse.prompts));
          } else if (Array.isArray(promptsResponse)) {
            setPrompts(promptsResponse);
          } else {
            setPrompts(['system-prompt', 'user-prompt', 'assistant-prompt']);
          }
        } catch (err) {
          console.error('Error fetching prompts:', err);
          toast.error('Failed to load prompts');
          setPrompts(['system-prompt', 'user-prompt', 'assistant-prompt']);
        }

        try {
          const envResponse = await api.getEnvironmentVariables(agentId, 'mini-agents', miniAgentId);
          
          if (envResponse && envResponse.environmentVariables) {
            setEnvironmentFunctions(Object.keys(envResponse.environmentVariables));
          } else if (Array.isArray(envResponse)) {
            setEnvironmentFunctions(envResponse);
          } else {
            setEnvironmentFunctions(['initialize', 'process', 'finalize', 'get-logs', 'web-interaction']);
          }
        } catch (err) {
          console.error('Error fetching functions:', err);
          toast.error('Failed to load environment functions');
          setEnvironmentFunctions(['initialize', 'process', 'finalize', 'get-logs', 'web-interaction']);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching miniAgent details:', error);
        setError('Failed to load mini agent details. Please try again.');
        setLoading(false);
      }
    };

    fetchMiniAgentDetails();
  }, [agentId, miniAgentId, navigate]);

  const fetchMiniConfigFiles = async (agentNumber) => {
    try {
      console.log(`Fetching mini-config files for agent number: ${agentNumber}`);
      
      const agentsResponse = await api.getAgents();
      let parentAgentData = null;
      
      if (agentsResponse && agentsResponse.success && agentsResponse.agents) {
        parentAgentData = agentsResponse.agents.find(a => 
          a.uniqueIdentifier === agentId || 
          a.agentName === agentId || 
          a.repoName === agentId
        );
      } else if (agentsResponse && agentsResponse.body && agentsResponse.body.agents) {
        parentAgentData = agentsResponse.body.agents.find(a => 
          a.uniqueIdentifier === agentId || 
          a.agentName === agentId || 
          a.repoName === agentId
        );
      } else if (agentsResponse && agentsResponse.agents) {
        parentAgentData = agentsResponse.agents.find(a => 
          a.uniqueIdentifier === agentId || 
          a.agentName === agentId || 
          a.repoName === agentId
        );
      }
      
      const payload = {
        awsRegion: parentAgentData?.awsRegion || "us-west-2",
        githubAccount: parentAgentData?.repoAccount || "materassi",
        repository: parentAgentData?.repoName || "AgentDeveloper",
        githubSecretName: "GitHubToken",
        awsAccount: parentAgentData?.awsAccountNumber || "851725399969",
        agentNumber: agentNumber,
        parentAgentName: parentAgentData?.agentName || parentAgentData?.repoName || "AgentDeveloper",
        isMiniAgent: true
      };
      
      const response = await api.makeAuthenticatedRequest('POST', `/mini-config/${agentNumber}`, payload);
      
      console.log('Mini-config response:', response);
      
      let config = extractFromResponse(response, 'config');
      let metadata = extractFromResponse(response, 'metadata');
      
      if (metadata) {
        setMetadataContent(metadata);
      }
      
      if (config) {
        setMiniConfigData(config);
        
        const fileNames = ['config.json', 'metadata.json'];
        
        if (typeof config === 'object') {
          Object.keys(config).forEach(key => {
            if (
              typeof config[key] === 'object' && 
              !Array.isArray(config[key]) && 
              key !== 'micro_agents' && 
              key !== 'settings' &&
              key !== 'metadata' &&
              !fileNames.includes(`${key}.json`)
            ) {
              fileNames.push(`${key}.json`);
            }
          });
          
          if (config.settings && !fileNames.includes('settings.json')) {
            fileNames.push('settings.json');
          }
        }
        
        if (fileNames.length === 0) {
          fileNames.push('config.json', 'settings.json', 'metadata.json');
        }
        
        setConfigFiles(fileNames);
      } else {
        setConfigFiles(['config.json', 'settings.json', 'metadata.json']);
      }
    } catch (err) {
      console.error('Error fetching mini-config files:', err);
      toast.error('Failed to load mini-agent configuration files');
      setConfigFiles(['config.json', 'settings.json', 'metadata.json']);
    }
  };

  useEffect(() => {
    if (!selectedPrompt) return;

    const fetchSelectedPromptContent = async () => {
      if (!selectedPrompt) return;
      
      try {
        setIsSaving(true);
        setShowPromptsEditor(true);
        const response = await api.getPromptContent(agentId, 'mini-agents', miniAgentId, selectedPrompt);
        
        if (typeof response === 'string') {
          setPromptContent(response);
        } else if (response && response.content) {
          setPromptContent(response.content);
        } else if (response && response.prompts && response.prompts[selectedPrompt]) {
          setPromptContent(response.prompts[selectedPrompt]);
        } else {
          setPromptContent('');
        }
        
        setIsSaving(false);
      } catch (error) {
        console.error('Error fetching prompt content:', error);
        toast.error('Failed to load prompt content');
        setPromptContent('');
        setIsSaving(false);
      }
    };

    fetchSelectedPromptContent();
  }, [agentId, miniAgentId, selectedPrompt]);

  useEffect(() => {
    if (!selectedFunction) return;

    const fetchEnvironmentVariables = async () => {
      if (!selectedFunction) return;

      try {
        setIsSaving(true);
        setShowVarsEditor(true);
        const response = await api.getEnvironmentVariables(agentId, 'mini-agents', miniAgentId, selectedFunction);
        
        if (response && response.environmentVariables && response.environmentVariables[selectedFunction]) {
          setEnvironmentVariables(response.environmentVariables[selectedFunction]);
        } else if (response && typeof response === 'object') {
          setEnvironmentVariables(response);
        } else {
          setEnvironmentVariables({});
        }
        
        setIsSaving(false);
      } catch (error) {
        console.error('Error fetching environment variables:', error);
        toast.error('Failed to load environment variables');
        setEnvironmentVariables({});
        setIsSaving(false);
      }
    };

    fetchEnvironmentVariables();
  }, [agentId, miniAgentId, selectedFunction]);

  useEffect(() => {
    if (!selectedConfigFile || !miniAgent?.agentNumber) return;

    const fetchConfigFileContent = async () => {
      try {
        setIsSaving(true);
        setShowConfigEditor(true);
        
        const agentNumber = miniAgent.agentNumber;
        let configData = null;
        
        if (selectedConfigFile === 'config.json') {
          configData = miniConfigData;
          if (configData && configData.metadata) {
            configData = {...configData};
            delete configData.metadata;
          }
        } else if (selectedConfigFile === 'metadata.json') {
          configData = metadataContent && Object.keys(metadataContent).length > 0
            ? metadataContent
            : (miniConfigData && miniConfigData.metadata ? miniConfigData.metadata : null);
        } else {
          const configKey = selectedConfigFile.replace('.json', '');
          
          if (miniConfigData && miniConfigData[configKey]) {
            configData = miniConfigData[configKey];
          } else {
            try {
              const payload = {
                awsRegion: parentAgent?.awsRegion || "us-west-2",
                githubAccount: parentAgent?.repoAccount || "materassi",
                repository: parentAgent?.repoName || "AgentDeveloper",
                githubSecretName: "GitHubToken",
                awsAccount: parentAgent?.awsAccount || "851725399969",
                agentNumber: agentNumber,
                fileName: selectedConfigFile,
                parentAgentName: parentAgent?.name || "AgentDeveloper",
                isMiniAgent: true
              };
              
              const response = await api.makeAuthenticatedRequest('POST', `/mini-config/${agentNumber}`, payload);
              configData = extractFromResponse(response, configKey) || extractFromResponse(response, 'config');
            } catch (fetchErr) {
              console.warn('Could not fetch config from API:', fetchErr);
              configData = null;
            }
          }
        }
        
        if (configData) {
          setConfigContent(JSON.stringify(configData, null, 2));
        } else {
          let defaultContent = {};
          if (selectedConfigFile === 'settings.json') {
            defaultContent = { timeout: 30000, retries: 3, maxTokens: 1000 };
          } else if (selectedConfigFile === 'metadata.json') {
            defaultContent = { 
              version: "1.0.0", 
              author: "Agent Developer", 
              lastUpdated: new Date().toISOString(),
              description: "Metadata for mini agent" 
            };
          } else {
            defaultContent = { enabled: true, version: "1.0.0" };
          }
          setConfigContent(JSON.stringify(defaultContent, null, 2));
        }
        
        setIsSaving(false);
      } catch (error) {
        console.error('Error fetching config file content:', error);
        toast.error('Failed to load configuration file');
        
        let defaultContent = {};
        if (selectedConfigFile === 'settings.json') {
          defaultContent = { timeout: 30000, retries: 3, maxTokens: 1000 };
        } else if (selectedConfigFile === 'metadata.json') {
          defaultContent = { 
            version: "1.0.0", 
            author: "Agent Developer", 
            lastUpdated: new Date().toISOString(),
            description: "Metadata for mini agent" 
          };
        } else {
          defaultContent = { enabled: true, version: "1.0.0" };
        }
        
        setConfigContent(JSON.stringify(defaultContent, null, 2));
        setIsSaving(false);
      }
    };

    fetchConfigFileContent();
  }, [miniAgent, selectedConfigFile, miniConfigData, agentId, metadataContent, parentAgent]);

  useEffect(() => {
    if (!miniAgent?.agentNumber) return;

    const fetchLambdaFunctions = async () => {
      try {
        setLoadingLambda(true);
        
        const payload = {
          parentAgentName: parentAgent?.name || parentAgent?.repoName || "AgentDeveloper",
          repository: parentAgent?.repoName || "AgentDeveloper",
          githubAccount: parentAgent?.repoAccount || "materassi",
          awsRegion: parentAgent?.awsRegion || "us-west-2",
          agentNumber: miniAgent.agentNumber
        };
        
        console.log('Fetching lambda functions with payload:', payload);
        
        const response = await api.makeAuthenticatedRequest('POST', '/lambda', payload);
        
        const lambdaFuncs = extractFromResponse(response, 'lambdaFunctions');
        
        if (lambdaFuncs && typeof lambdaFuncs === 'object') {
          setLambdaFunctions(lambdaFuncs);
          console.log('Lambda functions loaded successfully:', Object.keys(lambdaFuncs).length);
        } else {
          console.warn('No lambda functions found in response');
          setLambdaFunctions({});
        }
        
        setLoadingLambda(false);
      } catch (error) {
        console.error('Error fetching lambda functions:', error);
        toast.error('Failed to load lambda functions');
        setLambdaFunctions({});
        setLoadingLambda(false);
      }
    };
    
    if (activeTab === 'lambda') {
      fetchLambdaFunctions();
    }
  }, [miniAgent, parentAgent, activeTab]);

  useEffect(() => {
    if (!selectedLambdaFunction || !lambdaFunctions[selectedLambdaFunction]) return;
    
    setLambdaFunctionCode(lambdaFunctions[selectedLambdaFunction].code || '');
    setShowLambdaEditor(true);
  }, [selectedLambdaFunction, lambdaFunctions]);

  useEffect(() => {
    if (!miniAgent?.agentNumber) return;

    const fetchStateMachine = async () => {
      try {
        setLoadingStateMachine(true);
        
        const payload = {
          parentAgentName: parentAgent?.name || parentAgent?.repoName || "AgentDeveloper",
          repository: parentAgent?.repoName || "AgentDeveloper",
          githubAccount: parentAgent?.repoAccount || "materassi",
          awsRegion: parentAgent?.awsRegion || "us-west-2",
          agentNumber: miniAgent.agentNumber,
          environment: selectedEnvironment
        };
        
        console.log('Fetching state machine with payload:', payload);
        
        const response = await api.makeAuthenticatedRequest('POST', '/stepfunction', payload);
        
        const stateMachineData = extractFromResponse(response, 'stateMachine');
        
        if (stateMachineData) {
          setStateMachine(stateMachineData);
          const mermaid = generateMermaidDiagram(stateMachineData.definition);
          setMermaidDiagram(mermaid);
          console.log('State machine loaded successfully');
        } else {
          console.warn('No state machine found in response');
          setStateMachine(null);
          setMermaidDiagram('');
        }
        
        setLoadingStateMachine(false);
      } catch (error) {
        console.error('Error fetching state machine:', error);
        toast.error('Failed to load state machine');
        setStateMachine(null);
        setMermaidDiagram('');
        setLoadingStateMachine(false);
      }
    };
    
    if (activeTab === 'stepfunction') {
      fetchStateMachine();
    }
  }, [miniAgent, parentAgent, activeTab, selectedEnvironment]);

  const generateMermaidDiagram = (definition) => {
    if (!definition || !definition.States) {
      return '';
    }
    
    let diagram = 'graph TD;\\\\\\\\n';
    const states = definition.States;
    const startAt = definition.StartAt;
    
    diagram += `  Start((Start)) --> ${startAt};\\\\\\\\n`;
    
    Object.entries(states).forEach(([stateName, stateConfig]) => {
      if (stateConfig.Type === 'Task') {
        let functionName = "Task";
        if (stateConfig.Parameters && stateConfig.Parameters.FunctionName) {
          const arnParts = stateConfig.Parameters.FunctionName.split(':');
          functionName = arnParts[arnParts.length - 1];
          if (functionName.length > 40) {
            functionName = functionName.substring(0, 37) + '...';
          }
        }
        
        diagram += `  ${stateName}[${stateName}<br/>${functionName}]`;
        
        if (stateConfig.Next) {
          diagram += ` --> ${stateConfig.Next};\\\\\\\\n`;
        } else {
          diagram += `;\\\\\\\\n`;
        }
        
        if (stateConfig.Catch) {
          stateConfig.Catch.forEach((catchBlock, index) => {
            if (catchBlock.Next) {
              diagram += `  ${stateName} -->|Error ${index+1}| ${catchBlock.Next};\\\\\\\\n`;
            }
          });
        }
        
        if (stateConfig.Retry && stateConfig.Retry.length > 0) {
          diagram += `  ${stateName} -->|Retry| ${stateName};\\\\\\\\n`;
        }
      } else if (stateConfig.Type === 'Choice') {
        if (stateConfig.Choices) {
          stateConfig.Choices.forEach((choice, index) => {
            if (choice.Next) {
              diagram += `  ${stateName}{${stateName}} -->|Choice ${index+1}| ${choice.Next};\\\\\\\\n`;
            }
          });
          
          if (stateConfig.Default) {
            diagram += `  ${stateName} -->|Default| ${stateConfig.Default};\\\\\\\\n`;
          }
        }
      } else if (stateConfig.Type === 'Parallel') {
        diagram += `  ${stateName}{{${stateName}}}`;
        
        if (stateConfig.Next) {
          diagram += ` --> ${stateConfig.Next};\\\\\\\\n`;
        } else {
          diagram += `;\\\\\\\\n`;
        }
        
        if (stateConfig.Branches && stateConfig.Branches.length > 0) {
          stateConfig.Branches.forEach((branch, index) => {
            if (branch.StartAt) {
              diagram += `  ${stateName} -->|Branch ${index+1}| ${branch.StartAt};\\\\\\\\n`;
            }
          });
        }
      } else if (stateConfig.Type === 'Map') {
        diagram += `  ${stateName}[/${stateName}/]`;
        
        if (stateConfig.Next) {
          diagram += ` --> ${stateConfig.Next};\\\\\\\\n`;
        } else {
          diagram += `;\\\\\\\\n`;
        }
      } else if (stateConfig.Type === 'Succeed') {
        diagram += `  ${stateName}((${stateName})) --> End((End));\\\\\\\\n`;
      } else if (stateConfig.Type === 'Fail') {
        diagram += `  ${stateName}[/${stateName}/] --> End((End));\\\\\\\\n`;
      } else if (stateConfig.Type === 'Wait') {
        diagram += `  ${stateName}[${stateName}]`;
        
        if (stateConfig.Next) {
          diagram += ` --> ${stateConfig.Next};\\\\\\\\n`;
        } else {
          diagram += `;\\\\\\\\n`;
        }
      } else {
        diagram += `  ${stateName}[${stateName}]`;
        
        if (stateConfig.Next) {
          diagram += ` --> ${stateConfig.Next};\\\\\\\\n`;
        } else {
          diagram += `;\\\\\\\\n`;
        }
      }
    });
    
    return diagram;
  };

  const handlePromptSelect = (promptName) => {
    setSelectedPrompt(promptName);
    setShowPromptsEditor(true);
    setActiveTab('prompts');
  };

  const handleFunctionSelect = (functionName) => {
    setSelectedFunction(functionName);
    setShowVarsEditor(true);
    setActiveTab('environment');
  };

  const handleConfigFileSelect = (fileName) => {
    setSelectedConfigFile(fileName);
    setShowConfigEditor(true);
    setActiveTab('config');
  };

  const handleLambdaFunctionSelect = (functionName) => {
    setSelectedLambdaFunction(functionName);
    setShowLambdaEditor(true);
    setActiveTab('lambda');
  };

  const handlePromptContentChange = (e) => {
    setPromptContent(e.target.value);
  };

  const handleEnvironmentVariableChange = (key, value) => {
    setEnvironmentVariables(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleConfigContentChange = (e) => {
    setConfigContent(e.target.value);
  };

  const handleAddEnvironmentVariable = () => {
    setEnvironmentVariables(prev => ({
      ...prev,
      '': ''
    }));
  };

  const handleRemoveEnvironmentVariable = (key) => {
    setEnvironmentVariables(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  const handleEnvironmentChange = (env) => {
    setSelectedEnvironment(env);
  };

  const handleSavePrompt = async () => {
    if (!selectedPrompt) return;

    try {
      setIsSaving(true);
      
      const agentNumber = String(miniAgent.agentNumber);
      if (!agentNumber || agentNumber.length !== 3) {
        console.error('Invalid agent number:', agentNumber);
        throw new Error(`Invalid agent number: ${agentNumber}. Must be a 3-digit number.`);
      }
      
      console.log(`Saving prompt '${selectedPrompt}' for agent number ${agentNumber}`);
      
      const payload = {
        awsRegion: parentAgent?.awsRegion || "us-west-2",
        githubAccount: parentAgent?.repoAccount || "materassi",
        repository: parentAgent?.repoName || "AgentDeveloper",
        githubSecretName: "GitHubToken",
        agentNumber: agentNumber,
        parentAgentName: parentAgent?.name || parentAgent?.repoName || "AgentDeveloper",
        isMiniAgent: true,
        files: {
          [selectedPrompt]: promptContent
        }
      };
      
      console.log('Prompt save payload:', JSON.stringify(payload));
      
      const response = await api.makeAuthenticatedRequest('PUT', `/prompts/${agentNumber}`, payload);
      console.log('Prompt save response:', JSON.stringify(response));
      
      const updateResults = extractFromResponse(response, 'updateResults');
      
      if (updateResults && Array.isArray(updateResults)) {
        const promptResult = updateResults.find(r => r.prompt_name === selectedPrompt);
        
        if (promptResult) {
          const githubSuccess = promptResult.github_update?.success;
          const devS3Success = promptResult.s3_update1?.success;
          const prodS3Success = promptResult.s3_update2?.success;
          
          if (githubSuccess && (devS3Success || prodS3Success)) {
            toast.success(`Prompt "${selectedPrompt}" saved successfully!`);
          } else if (githubSuccess) {
            toast.success(`Prompt "${selectedPrompt}" saved to GitHub. Note: S3 bucket updates may require deployment first.`);
          } else if (devS3Success || prodS3Success) {
            toast.success(`Prompt "${selectedPrompt}" saved to S3, but GitHub update failed.`);
          } else {
            throw new Error('Failed to save prompt to any location');
          }
        } else {
          toast.success(`Prompt "${selectedPrompt}" update processed, but no specific results returned.`);
        }
        
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else if (response && response.success) {
        toast.success(`Prompt "${selectedPrompt}" saved successfully!`);
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else if (response) {
        toast.success(`Prompt "${selectedPrompt}" saved successfully!`);
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        throw new Error('Unrecognized response format when saving prompt');
      }
      
      setIsSaving(false);
    } catch (error) {
      console.error('Error saving prompt:', error);
      toast.error('Failed to save prompt content: ' + (error.message || 'Unknown error'));
      setIsSaving(false);
    }
  };

  const handleSaveEnvironmentVariables = async () => {
    if (!selectedFunction) return;

    try {
      setIsSaving(true);
      
      const agentNumber = String(miniAgent.agentNumber);
      if (!agentNumber || agentNumber.length !== 3) {
        console.error('Invalid agent number:', agentNumber);
        throw new Error(`Invalid agent number: ${agentNumber}. Must be a 3-digit number.`);
      }
      
      console.log(`Saving environment variables for function '${selectedFunction}' for agent number ${agentNumber}`);
      
      const payload = {
        awsRegion: parentAgent?.awsRegion || "us-west-2",
        githubAccount: parentAgent?.repoAccount || "materassi",
        repository: parentAgent?.repoName || "AgentDeveloper",
        githubSecretName: "GitHubToken",
        agentNumber: agentNumber,
        parentAgentName: parentAgent?.name || parentAgent?.repoName || "AgentDeveloper",
        files: {
          [selectedFunction]: environmentVariables
        }
      };
      
      console.log('Environment variables save payload:', JSON.stringify(payload));
      
      const response = await api.makeAuthenticatedRequest('PUT', `/environment/${agentNumber}`, payload);
      console.log('Environment variables save response:', JSON.stringify(response));
      
      const updateResults = extractFromResponse(response, 'updateResults');
      
      if (updateResults && Array.isArray(updateResults)) {
        const functionResult = updateResults.find(r => r.function_name === selectedFunction);
        
        if (functionResult) {
          const githubSuccess = functionResult.github_update?.github_success;
          const anyAwsSuccess = functionResult.aws_updates?.some(u => u.aws_success);
          
          if (githubSuccess && anyAwsSuccess) {
            toast.success(`Environment variables for "${selectedFunction}" saved successfully!`);
          } else if (githubSuccess) {
            toast.success(`Environment variables for "${selectedFunction}" saved to GitHub. Some AWS Lambda updates may require deployment first.`);
          } else if (anyAwsSuccess) {
            toast.success(`Environment variables for "${selectedFunction}" updated in AWS, but GitHub update failed.`);
          } else {
            throw new Error('Failed to save environment variables to any location');
          }
        } else {
          toast.success(`Environment variables for "${selectedFunction}" update processed, but no specific results returned.`);
        }
        
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else if (response && response.success) {
        toast.success(`Environment variables for "${selectedFunction}" saved successfully!`);
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else if (response) {
        toast.success(`Environment variables for "${selectedFunction}" saved successfully!`);
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        throw new Error('Unrecognized response format when saving environment variables');
      }
      
      setIsSaving(false);
    } catch (error) {
      console.error('Error saving environment variables:', error);
      toast.error('Failed to save environment variables: ' + (error.message || 'Unknown error'));
      setIsSaving(false);
    }
  };

  const handleSaveConfigFile = async () => {
    if (!selectedConfigFile || !miniAgent?.agentNumber) return;

    try {
      setIsSaving(true);
      
      let configData;
      try {
        configData = JSON.parse(configContent.trim());
      } catch (e) {
        toast.error('Invalid JSON in configuration file');
        setIsSaving(false);
        return;
      }
      
      const agentNumber = String(miniAgent.agentNumber);
      if (!agentNumber || agentNumber.length !== 3) {
        console.error('Invalid agent number:', agentNumber);
        throw new Error(`Invalid agent number: ${agentNumber}. Must be a 3-digit number.`);
      }
      
      console.log(`Saving config file '${selectedConfigFile}' for agent number ${agentNumber}`);
      
      const payload = {
        agentNumber: agentNumber,
        parentAgentName: parentAgent?.name || parentAgent?.repoName || "AgentDeveloper",
        repository: parentAgent?.repoName || "AgentDeveloper",
        githubAccount: parentAgent?.repoAccount || "materassi",
        awsRegion: parentAgent?.awsRegion || "us-west-2",
        githubSecretName: "GitHubToken",
        isMiniAgent: true
      };
      
      if (selectedConfigFile === 'config.json') {
        payload.fileType = "config";
        payload.config = configData;
      } else if (selectedConfigFile === 'metadata.json') {
        payload.fileType = "metadata";
        payload.metadata = configData;
      } else {
        const configKey = selectedConfigFile.replace('.json', '');
        payload.fileType = configKey;
        payload[configKey] = configData;
      }
      
      console.log('Config save payload:', JSON.stringify(payload));
      
      const response = await api.makeAuthenticatedRequest('PUT', `/mini-config/${agentNumber}`, payload);
      
      console.log('Config save response:', response);
      
      const updateResult = extractFromResponse(response, 'updateResult');
      
      if (updateResult) {
        const githubSuccess = updateResult.github_update?.success;
        const devS3Success = updateResult.s3_update1?.success;
        const prodS3Success = updateResult.s3_update2?.success;
        
        if (githubSuccess && (devS3Success || prodS3Success)) {
          toast.success(`Configuration "${selectedConfigFile}" saved successfully!`);
        } else if (githubSuccess) {
          toast.success(`Configuration "${selectedConfigFile}" saved to GitHub. Note: S3 bucket updates may require deployment first.`);
        } else if (devS3Success || prodS3Success) {
          toast.success(`Configuration "${selectedConfigFile}" saved to S3, but GitHub update failed.`);
        } else {
          throw new Error('Failed to save configuration to any location');
        }
        
        let updatedMiniConfigData = {...miniConfigData};
        
        if (selectedConfigFile === 'config.json') {
          if (miniConfigData.metadata && !configData.metadata) {
            configData.metadata = miniConfigData.metadata;
          }
          setMiniConfigData(configData);
        } else if (selectedConfigFile === 'metadata.json') {
          updatedMiniConfigData.metadata = configData;
          setMiniConfigData(updatedMiniConfigData);
        } else {
          const configKey = selectedConfigFile.replace('.json', '');
          updatedMiniConfigData[configKey] = configData;
          setMiniConfigData(updatedMiniConfigData);
        }
        
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else if (response && response.success) {
        toast.success(`Configuration "${selectedConfigFile}" saved successfully!`);
        
        let updatedMiniConfigData = {...miniConfigData};
        
        if (selectedConfigFile === 'config.json') {
          if (miniConfigData.metadata && !configData.metadata) {
            configData.metadata = miniConfigData.metadata;
          }
          setMiniConfigData(configData);
        } else if (selectedConfigFile === 'metadata.json') {
          updatedMiniConfigData.metadata = configData;
          setMiniConfigData(updatedMiniConfigData);
        } else {
          const configKey = selectedConfigFile.replace('.json', '');
          updatedMiniConfigData[configKey] = configData;
          setMiniConfigData(updatedMiniConfigData);
        }
        
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else if (response) {
        toast.success(`Configuration "${selectedConfigFile}" saved successfully!`);
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        const errorMessage = 
          response?.error || 
          response?.message || 
          'Failed to save configuration';
        throw new Error(errorMessage);
      }
      
    } catch (error) {
      console.error('Error saving config file:', error);
      toast.error('Failed to save configuration: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeployToDev = async () => {
    if (!miniAgent?.agentNumber) return;
    
    try {
      setIsDeploying(prev => ({ ...prev, dev: true }));
      
      let cdkQualifier = "agentdev";
      
      if (parentAgent && parentAgent.cdkQualifier) {
        cdkQualifier = parentAgent.cdkQualifier;
        console.log('Using cdkQualifier from parent agent:', cdkQualifier);
      } else {
        try {
          const agentsResponse = await api.getAgents();
          const agents = agentsResponse?.agents || agentsResponse?.body?.agents || [];
          const agent = agents.find(a => 
            a.uniqueIdentifier === agentId || 
            a.agentName === agentId || 
            a.repoName === agentId
          );
          
          if (agent && agent.cdkQualifier) {
            cdkQualifier = agent.cdkQualifier;
            console.log('Fetched cdkQualifier from agents endpoint:', cdkQualifier);
          }
        } catch (err) {
          console.warn('Error fetching cdkQualifier from agents endpoint:', err);
        }
      }
      
      const payload = {
        awsRegion: parentAgent?.awsRegion || "us-west-2",
        githubAccount: parentAgent?.repoAccount || "materassi",
        repository: parentAgent?.repoName || "AgentDeveloper",
        githubSecretName: parentAgent?.githubTokenSecretName || "GitHubToken",
        awsAccount: parentAgent?.awsAccount || "851725399969",
        agentNumber: miniAgent.agentNumber,
        deploymentType: "agent",
        cdkQualifier: cdkQualifier,
        environment: "dev",
        buildDocker: "false"
      };
      
      const response = await api.makeAuthenticatedRequest('POST', '/deploy', payload);
      
      if (response && response.success) {
        toast.success('Deployment to development environment initiated successfully!');
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        throw new Error('Failed to initiate deployment');
      }
    } catch (error) {
      console.error('Error deploying to dev:', error);
      toast.error('Failed to deploy to development environment');
    } finally {
      setIsDeploying(prev => ({ ...prev, dev: false }));
    }
  };
  
  const handleDeployToProd = async () => {
    if (!miniAgent?.agentNumber) return;
    
    try {
      setIsDeploying(prev => ({ ...prev, prod: true }));
      
      let cdkQualifier = "agentdev";
      
      if (parentAgent && parentAgent.cdkQualifier) {
        cdkQualifier = parentAgent.cdkQualifier;
        console.log('Using cdkQualifier from parent agent:', cdkQualifier);
      } else {
        try {
          const agentsResponse = await api.getAgents();
          const agents = agentsResponse?.agents || agentsResponse?.body?.agents || [];
          const agent = agents.find(a => 
            a.uniqueIdentifier === agentId || 
            a.agentName === agentId || 
            a.repoName === agentId
          );
          
          if (agent && agent.cdkQualifier) {
            cdkQualifier = agent.cdkQualifier;
            console.log('Fetched cdkQualifier from agents endpoint:', cdkQualifier);
          }
        } catch (err) {
          console.warn('Error fetching cdkQualifier from agents endpoint:', err);
        }
      }
      
      const payload = {
        awsRegion: parentAgent?.awsRegion || "us-west-2",
        githubAccount: parentAgent?.repoAccount || "materassi",
        repository: parentAgent?.repoName || "AgentDeveloper",
        githubSecretName: parentAgent?.githubTokenSecretName || "GitHubToken",
        awsAccount: parentAgent?.awsAccount || "851725399969",
        agentNumber: miniAgent.agentNumber,
        deploymentType: "agent",
        cdkQualifier: cdkQualifier,
        environment: "prod",
        buildDocker: "false"
      };
      
      const response = await api.makeAuthenticatedRequest('POST', '/deploy', payload);
      
      if (response && response.success) {
        toast.success('Deployment to production environment initiated successfully!');
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        throw new Error('Failed to initiate deployment');
      }
    } catch (error) {
      console.error('Error deploying to prod:', error);
      toast.error('Failed to deploy to production environment');
    } finally {
      setIsDeploying(prev => ({ ...prev, prod: false }));
    }
  };

  const handleGoBackToParent = () => {
    navigate(`/parent/${agentId}`);
  };

  const handleGoToTesting = () => {
    navigate(`/agents/${agentId}/mini-agents/${miniAgentId}/testing`);
  };

  const handleGoToLogs = () => {
    navigate(`/agents/${agentId}/mini-agents/${miniAgentId}/logs`);
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    
    if (tabKey === 'environment') {
      setShowPromptsEditor(false);
      setSelectedPrompt(null);
      setShowConfigEditor(false);
      setSelectedConfigFile(null);
      setShowLambdaEditor(false);
      setSelectedLambdaFunction(null);
    } else if (tabKey === 'prompts') {
      setShowVarsEditor(false);
      setSelectedFunction(null);
      setShowConfigEditor(false);
      setSelectedConfigFile(null);
      setShowLambdaEditor(false);
      setSelectedLambdaFunction(null);
    } else if (tabKey === 'config') {
      setShowVarsEditor(false);
      setSelectedFunction(null);
      setShowPromptsEditor(false);
      setSelectedPrompt(null);
      setShowLambdaEditor(false);
      setSelectedLambdaFunction(null);
    } else if (tabKey === 'lambda') {
      setShowVarsEditor(false);
      setSelectedFunction(null);
      setShowPromptsEditor(false);
      setSelectedPrompt(null);
      setShowConfigEditor(false);
      setSelectedConfigFile(null);
    }
  };

  const clearSelectedItems = () => {
    if (activeTab === 'environment') {
      setSelectedFunction(null);
      setShowVarsEditor(false);
    } else if (activeTab === 'prompts') {
      setSelectedPrompt(null);
      setShowPromptsEditor(false);
    } else if (activeTab === 'config') {
      setSelectedConfigFile(null);
      setShowConfigEditor(false);
    } else if (activeTab === 'lambda') {
      setSelectedLambdaFunction(null);
      setShowLambdaEditor(false);
    }
  };

  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  if (loading) {
    return (
      <Container className="micro-agent-dev-container">
        <div className="loading-container">
          <Spinner />
          <p className="mt-3">Loading mini agent details...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="micro-agent-dev-container">
        <div className="error-container">
          <h4 className="error-heading">Error Loading Mini Agent</h4>
          <p>{error}</p>
          <hr />
          <div className="d-flex justify-content-end">
            <Button variant="primary" onClick={handleGoBackToParent}>
              <FaArrowLeft className="me-2" />
              Back to Parent Agent
            </Button>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="micro-agent-dev-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="agent-info-header">
        <div className="agent-title-container">
          <h1 className="agent-title">
            {miniAgent?.name ? capitalizeFirstLetter(miniAgent.name) : 'Unnamed Agent'}
          </h1>
          <p className="agent-description">{miniAgent?.description || 'No description available'}</p>
        </div>
        
        <div className="agent-meta-container">
          <div className="agent-meta-row">
            <div className="meta-item">
              <span className="meta-label">Parent Agent:</span>
              <span className="meta-value">{parentAgent?.name || miniAgent?.parentAgentName}</span>
            </div>
            
            <div className="meta-item">
              <span className="meta-label">Agent Number:</span>
              <span className="meta-value">
                <span className="agent-number-badge">{miniAgent?.agentNumber || '000'}</span>
              </span>
            </div>
            
            <div className="meta-item">
              <span className="meta-label">Status:</span>
              <span className="meta-value">
                {miniAgent?.commissioned ? 'Commissioned' : 'Not Commissioned'}
              </span>
            </div>
          </div>
          
          <div className="deployment-status-row">
            <div className="deployment-status">
              <span className="status-label">Development:</span>
              <Badge bg={miniAgent?.devStatus === 'deployed' ? 'success' : 'secondary'} className="status-badge">
                {miniAgent?.devStatus === 'deployed' ? 'Deployed' : 'Not Deployed'}
              </Badge>
              <Button 
                variant="primary" 
                size="sm" 
                className="deploy-button"
                onClick={handleDeployToDev}
                disabled={isDeploying.dev}
              >
                {isDeploying.dev ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Deploying...
                  </>
                ) : (
                  <>
                    <FaServer className="me-1" /> Deploy to Dev
                  </>
                )}
              </Button>
            </div>
            
            <div className="deployment-status">
              <span className="status-label">Production:</span>
              <Badge bg={miniAgent?.prodStatus === 'deployed' ? 'success' : 'secondary'} className="status-badge">
                {miniAgent?.prodStatus === 'deployed' ? 'Deployed' : 'Not Deployed'}
              </Badge>
              <Button 
                variant="danger" 
                size="sm" 
                className="deploy-button"
                onClick={handleDeployToProd}
                disabled={isDeploying.prod}
              >
                {isDeploying.prod ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Deploying...
                  </>
                ) : (
                  <>
                    <FaServer className="me-1" /> Deploy to Prod
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
        
        <div className="action-buttons-container">
          <Button variant="outline-light" onClick={handleGoBackToParent} className="action-button">
            <FaArrowLeft className="me-2" />
            Back to Parent Agent
          </Button>
          
          <Button variant="outline-light" onClick={handleGoToTesting} className="action-button">
            <FaVial className="me-2" />
            Test Agent
          </Button>
          
          <Button variant="outline-light" onClick={handleGoToLogs} className="action-button">
            <FaHistory className="me-2" />
            View Logs
          </Button>
        </div>
      </div>

      <div className="content-container">
        <Nav variant="tabs" activeKey={activeTab} onSelect={handleTabChange} className="main-tabs">
          <Nav.Item>
            <Nav.Link eventKey="prompts">Prompts</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="environment">Environment Variables</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="config">Configuration</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="lambda">Lambda Functions</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="stepfunction">Step Function</Nav.Link>
          </Nav.Item>
        </Nav>

        {activeTab === 'prompts' && (
          <div className="tab-content-container">
            <div className={`sidebar ${showPromptsEditor ? 'with-editor' : 'full-width'}`}>
              <div className="sidebar-header">
                <h3><FaFileAlt className="me-2" />Prompt Files</h3>
              </div>
              <div className="prompt-list">
                {prompts.map((prompt) => (
                  <div
                    key={prompt}
                    className={`prompt-item ${selectedPrompt === prompt ? 'active' : ''}`}
                    onClick={() => handlePromptSelect(prompt)}
                  >
                    <div className="prompt-name">{prompt}</div>
                  </div>
                ))}
              </div>
              <div className="sidebar-help-text">
                Select a prompt file to view or edit it
              </div>
            </div>
            
            {showPromptsEditor && selectedPrompt && (
              <div className="editor-container">
                <div className="editor-header">
                  <h3>Editing Prompt: {selectedPrompt}</h3>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={clearSelectedItems}
                    className="close-editor-btn"
                  >
                    <FaTimes />
                  </Button>
                </div>
                
                <div className="editor-content">
                  {isSaving ? (
                    <div className="loading-container py-4">
                      <Spinner />
                      <p>Loading prompt content...</p>
                    </div>
                  ) : (
                    <Form.Control
                      as="textarea"
                      value={promptContent}
                      onChange={handlePromptContentChange}
                      className="prompt-editor"
                      rows={20}
                      spellCheck={false}
                    />
                  )}
                </div>
                
                <div className="editor-footer">
                  <Button 
                    variant="primary" 
                    onClick={handleSavePrompt}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Save Prompt
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'environment' && (
          <div className="tab-content-container">
            <div className={`sidebar ${showVarsEditor ? 'with-editor' : 'full-width'}`}>
              <div className="sidebar-header">
                <h3><FaCode className="me-2" />Functions</h3>
              </div>
              <div className="function-list">
                {environmentFunctions.map((func) => (
                  <div
                    key={func}
                    className={`function-item ${selectedFunction === func ? 'active' : ''}`}
                    onClick={() => handleFunctionSelect(func)}
                  >
                    <div className="function-name">{func}</div>
                  </div>
                ))}
              </div>
              <div className="sidebar-help-text">
                Select a function to view or edit its environment variables
              </div>
            </div>
            
            {showVarsEditor && selectedFunction && (
              <div className="editor-container">
                <div className="editor-header">
                  <h3>Environment Variables: {selectedFunction}</h3>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={clearSelectedItems}
                    className="close-editor-btn"
                  >
                    <FaTimes />
                  </Button>
                </div>
                
                <div className="editor-content">
                  <div className="toolbar mb-3">
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={handleAddEnvironmentVariable}
                      className="add-variable-btn"
                    >
                      <FaPlus className="me-1" /> Add Variable
                    </Button>
                  </div>
                  
                  {isSaving ? (
                    <div className="loading-container py-4">
                      <Spinner />
                      <p>Loading variables...</p>
                    </div>
                  ) : Object.keys(environmentVariables).length > 0 ? (
                    <div className="variables-table-container">
                      <Table bordered className="variables-table">
                        <thead>
                          <tr>
                            <th width="40%">Key</th>
                            <th width="50%">Value</th>
                            <th width="10%">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(environmentVariables).map(([key, value], index) => (
                            <tr key={index}>
                              <td>
                                <Form.Control
                                  type="text"
                                  value={key}
                                  onChange={(e) => {
                                    const newKey = e.target.value;
                                    const newVariables = { ...environmentVariables };
                                    delete newVariables[key];
                                    newVariables[newKey] = value;
                                    setEnvironmentVariables(newVariables);
                                  }}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="text"
                                  value={value}
                                  onChange={(e) => handleEnvironmentVariableChange(key, e.target.value)}
                                />
                              </td>
                              <td className="text-center">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleRemoveEnvironmentVariable(key)}
                                >
                                  <FaTimes />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No environment variables defined for this function.</p>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleAddEnvironmentVariable}
                      >
                        <FaPlus className="me-1" /> Add First Variable
                      </Button>
                    </div>
                  )}
                </div>
                
                <div className="editor-footer">
                  <Button 
                    variant="primary" 
                    onClick={handleSaveEnvironmentVariables}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Save Environment Variables
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'config' && (
          <div className="tab-content-container">
            <div className={`sidebar ${showConfigEditor ? 'with-editor' : 'full-width'}`}>
              <div className="sidebar-header">
                <h3><FaCogs className="me-2" />Configuration Files</h3>
              </div>
              <div className="prompt-list">
                {configFiles.map((file) => (
                  <div
                    key={file}
                    className={`prompt-item ${selectedConfigFile === file ? 'active' : ''}`}
                    onClick={() => handleConfigFileSelect(file)}
                  >
                    <div className="prompt-name">{file}</div>
                  </div>
                ))}
              </div>
              <div className="sidebar-help-text">
                Select a configuration file to view or edit it
              </div>
            </div>
            
            {showConfigEditor && selectedConfigFile && (
              <div className="editor-container">
                <div className="editor-header">
                  <h3>Editing Config: {selectedConfigFile}</h3>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={clearSelectedItems}
                    className="close-editor-btn"
                  >
                    <FaTimes />
                  </Button>
                </div>
                
                <div className="editor-content">
                  {isSaving ? (
                    <div className="loading-container py-4">
                      <Spinner />
                      <p>Loading configuration content...</p>
                    </div>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small>
                          {isValidJson(configContent) ? (
                            <span className="text-success">
                              <FaCheckCircle className="me-1" />
                              Valid JSON
                            </span>
                          ) : (
                            <span className="text-danger">
                              <FaTimesCircle className="me-1" />
                              Invalid JSON
                            </span>
                          )}
                        </small>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleFormatJson}
                          disabled={!configContent}
                        >
                          <FaCode className="me-1" />
                          Format JSON
                        </Button>
                      </div>
                      <Form.Control
                        as="textarea"
                        value={configContent}
                        onChange={handleConfigContentChange}
                        onKeyDown={handleConfigKeyDown}
                        className="prompt-editor"
                        rows={20}
                        style={{ 
                          fontFamily: 'monospace',
                          fontSize: '13px',
                          whiteSpace: 'pre',
                          overflowWrap: 'normal',
                          overflowX: 'auto'
                        }}
                        isInvalid={configContent && !isValidJson(configContent)}
                        spellCheck={false}
                      />
                      {configContent && !isValidJson(configContent) && (
                        <Form.Text className="text-danger mt-2">
                          Invalid JSON format. Click "Format JSON" to auto-fix indentation,
                          or check for missing commas/brackets.
                        </Form.Text>
                      )}
                    </>
                  )}
                </div>
                
                <div className="editor-footer">
                  <Button 
                    variant="primary" 
                    onClick={handleSaveConfigFile}
                    disabled={isSaving || (configContent && !isValidJson(configContent))}
                  >
                    {isSaving ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Save Configuration
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'lambda' && (
          <div className="tab-content-container">
            <div className={`sidebar ${showLambdaEditor ? 'with-editor' : 'full-width'}`}>
              <div className="sidebar-header">
                <h3><FaCode className="me-2" />Lambda Functions</h3>
              </div>
              <div className="function-list">
                {loadingLambda ? (
                  <div className="p-4 text-center">
                    <Spinner size="sm" />
                    <p className="mt-2">Loading lambda functions...</p>
                  </div>
                ) : Object.keys(lambdaFunctions).length > 0 ? (
                  Object.keys(lambdaFunctions).map((func) => (
                    <div
                      key={func}
                      className={`function-item ${selectedLambdaFunction === func ? 'active' : ''}`}
                      onClick={() => handleLambdaFunctionSelect(func)}
                    >
                      <div className="function-name">{func}</div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center">
                    <p>No lambda functions found.</p>
                  </div>
                )}
              </div>
              <div className="sidebar-help-text">
                Select a lambda function to view its code
              </div>
            </div>
            
            {showLambdaEditor && selectedLambdaFunction && (
              <div className="editor-container">
                <div className="editor-header">
                  <h3>Lambda Function: {selectedLambdaFunction}</h3>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={clearSelectedItems}
                    className="close-editor-btn"
                  >
                    <FaTimes />
                  </Button>
                </div>
                
                <div className="editor-content">
                  {loadingLambda ? (
                    <div className="loading-container py-4">
                      <Spinner />
                      <p>Loading lambda function code...</p>
                    </div>
                  ) : (
                    <>
                      <div className="file-path mb-2 text-muted">
                        <small>
                          {lambdaFunctions[selectedLambdaFunction]?.path || 'Path not available'}
                        </small>
                      </div>
                      <Form.Control
                        as="textarea"
                        value={lambdaFunctionCode}
                        className="prompt-editor"
                        rows={25}
                        readOnly
                        style={{
                          fontFamily: 'monospace',
                          fontSize: '13px',
                          whiteSpace: 'pre',
                          overflowWrap: 'normal',
                          overflowX: 'auto'
                        }}
                        spellCheck={false}
                      />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'stepfunction' && (
          <div className="p-4">
            <Card className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Step Function</h3>
                <div className="d-flex align-items-center">
                  <Form.Select 
                    value={selectedEnvironment}
                    onChange={(e) => handleEnvironmentChange(e.target.value)}
                    style={{ width: '150px' }}
                    className="me-3"
                  >
                    <option value="DEV">Development</option>
                    <option value="PROD">Production</option>
                  </Form.Select>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => {
                      setStateMachine(null);
                      setTimeout(() => {
                        const payload = {
                          parentAgentName: parentAgent?.name || parentAgent?.repoName || "AgentDeveloper",
                          repository: parentAgent?.repoName || "AgentDeveloper",
                          githubAccount: parentAgent?.repoAccount || "materassi",
                          awsRegion: parentAgent?.awsRegion || "us-west-2",
                          agentNumber: miniAgent.agentNumber,
                          environment: selectedEnvironment
                        };
                        api.makeAuthenticatedRequest('POST', '/stepfunction', payload)
                          .then(response => {
                            const stateMachineData = extractFromResponse(response, 'stateMachine');
                            if (stateMachineData) {
                              setStateMachine(stateMachineData);
                              const mermaid = generateMermaidDiagram(stateMachineData.definition);
                              setMermaidDiagram(mermaid);
                            }
                          })
                          .catch(error => {
                            console.error('Error refreshing state machine:', error);
                            toast.error('Failed to refresh state machine');
                          });
                      }, 100);
                    }}
                  >
                    <FaSync className="me-2" />
                    Refresh
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {loadingStateMachine ? (
                  <div className="loading-container py-4">
                    <Spinner />
                    <p>Loading state machine...</p>
                  </div>
                ) : stateMachine ? (
                  <div>
                    <div className="mb-4">
                      <div className="mb-3">
                        <strong>Name:</strong> {stateMachine.name}
                      </div>
                      <div className="mb-3">
                        <strong>ARN:</strong> {stateMachine.arn}
                      </div>
                      <div className="mb-3">
                        <strong>Status:</strong>{' '}
                        <Badge bg={stateMachine.status === 'ACTIVE' ? 'success' : 'secondary'}>
                          {stateMachine.status}
                        </Badge>
                      </div>
                      <div className="mb-3">
                        <strong>Creation Date:</strong> {new Date(stateMachine.creationDate).toLocaleString()}
                      </div>
                    </div>
                    
                    <h4 className="mb-3">State Machine Diagram</h4>
                    <div style={{ border: '1px solid #dee2e6', borderRadius: '4px', padding: '20px', background: '#f8f9fa' }}>
                      {mermaidDiagram ? (
                        <Mermaid chart={mermaidDiagram} />
                      ) : (
                        <div className="text-center">
                          <p>Failed to generate diagram</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-4">
                      <h4 className="mb-3">Definition (JSON)</h4>
                      <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                        <pre style={{ background: '#f8f9fa', padding: '15px', borderRadius: '4px', whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(stateMachine.definition, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p>No state machine found for this agent in the {selectedEnvironment} environment.</p>
                    <p className="text-muted">Try switching the environment or check if the agent has been deployed.</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        )}
      </div>
    </Container>
  );
};

export default MiniAgentDevelopment;